import React, { useState } from 'react';
import classNames from 'classnames';
import {
  FDBodyText,
  FDEmphasisSurface,
  FDHeadlineText,
  FDPaper,
  FDPrimaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import useResource from 'Hooks/useResource';
import { dataAttrs, DataValueMap, testAttr } from 'Helpers/Testing';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { Richtext } from 'Packages/FacetDesign/Components/Richtext';
import { boolTrue } from 'Consts/Strings';
import { TaxProductHeader } from './TaxProductHeader';
import { ProductTags } from './ProductTags';
import { ProductDocument } from './ProductDocument';

export const productMetaOverrideSet = buildFacetDesignOverride()
  .nameOverride(['title'], {
    _component: () => FDHeadlineText,
  }, overrideReadOnly)
  .override({ name: 'firstYearAmount', hint: 'richtext' }, {
    _component: () => Richtext,
    _customProps: {
      size: 'md', color: 'textSecondary', Component: FDHeadlineText,
    },
  }, overrideReadOnly)
  .nameOverride(['firstYearAmountWithDiscount'], {
    _component: () => FDHeadlineText,
    _customProps: { size: 'md' },
  }, overrideReadOnly)
  .nameOverride('document', {
    _component: () => ProductDocument,
  }, overrideReadOnly);


const useStyles = buildUseStyles('ProductActions', ((theme) => ({
  successButton: {
    '& > .FD-MuiButton-root': {
      '&:hover': {
        backgroundColor: theme.palette.facet.mint30p,
        color: theme.palette.facet.mint100p,
      },
      backgroundColor: theme.palette.facet.mint30p,
      color: theme.palette.facet.mint100p,
    },
  },
})));


export const TaxProduct = ({
  resource: productResourceProp,
}) => {
  const { pageContentResource, loadPageContentLink } = usePageContentContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { handleChange } = useMetaLayoutContext();
  const { resource: productResource, setAttribute } = useResource(productResourceProp);
  const classes = useStyles();
  const isFullService = productResource.attr('product') === 'full-service';
  const isSelected = pageContentResource.attr('product') === productResource.id();
  const Component = (isFullService) ? FDEmphasisSurface : FDPaper;
  const label = productResource.attr('primaryActionLabel');

  const actionRel = productResource.withRelationship(
    fr.relationships.actionLink.name,
    (rel) => rel,
    () => null,
  );

  const onClick = () => {
    setIsSubmitting(true);

    if (actionRel !== null) {
      loadPageContentLink(actionRel.link('related'));
    } else if (!isSelected) {
      handleChange('product', productResource.id());
      handleChange('document', productResource.attr('document'));
    }

    setIsSubmitting(false);
  };

  return (
    <Component
      {...DataValueMap.init(`product-${productResource.attr('product')}${isSelected ? '-selected' : ''}`).build()}
    >
      <FDStack direction={'column'} justifyContent={'space-between'} spacing={2} padding={3} maxWidth={'500px'} minHeight={'270px'}>
        <FDStack height={'100%'} direction={'column'} justifyContent={'flex-start'} spacing={2}>
          <ProductTags
            resource={productResource}
            value={productResource.attr('tags')}
            color={productResource.attr('purchased') === boolTrue ? 'subtle' : 'warning'}
          />
          <FDStack direction="column" spacing={1}>
            <TaxProductHeader metaOverrideSet={productMetaOverrideSet} productResource={productResource} />
            <MetaControl
              resource={productResource}
              attrName={'document'}
              metaOverrideSet={productMetaOverrideSet}
              onChange={(val) => {
                setAttribute('document', val);
                handleChange('document', val);
              }}
              CustomProps={{
                isSelected,
              }}
            />
            <FDBodyText
              size={'md'}
              value={productResource.attr('description')}
              color={productResource.attr('purchased') === boolTrue ? 'textDisabled' : 'textPrimary'}
            />
          </FDStack>
        </FDStack>
        <div className={classNames({
          [classes.successButton]: isFullService,
        })}
        >
          {actionRel !== null && (
            <FDPrimaryActionButton
              {...dataAttrs('product-selection-btn', {
                [testAttr.dataValue]: isSelected,
              })}
              loading={isSubmitting}
              label={label}
              onClick={onClick}
            />
          )}
        </div>
      </FDStack>
    </Component>
  );
};

TaxProduct.propTypes = {
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
};
